import React, { useEffect, useState } from "react";
import {
  Typography,
  Divider,
  Card,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
} from "@mhome/ui";
import { useMediaQuery } from "@mui/material";
import { scopePreferenceState, activeScopeState } from "../global-state";
import { useRecoilState, useRecoilValue } from "recoil";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import { useWebSocket } from "../ws/websocket";
import { useToast } from "../common/toast";
import { saveScopePereference } from "../service/scope-service";
import { spacingToPx, convertSxToStyle } from "../lib/utils";

const Preference = () => {
  const [scopePreference, setScopePreference] =
    useRecoilState(scopePreferenceState);
  const activeScope = useRecoilValue(activeScopeState);
  const { successToast, errorToast } = useToast();
  const { sendAndWaitWs } = useWebSocket();
  const getComponentBgColor = useComponentBackgroundColor();
  const isSmallScreen = useMediaQuery("@media (max-width:599px)");

  // Helper function to get current scope preference
  const getCurrentScopePreference = () => {
    return scopePreference?.find((sp) => sp.scopeId === activeScope) || {};
  };

  // Helper function to update scope preference list
  const updateScopePreferenceList = (newSp) => {
    const existingIndex = scopePreference?.findIndex(
      (sp) => sp.scopeId === activeScope
    );
    if (existingIndex >= 0) {
      const updatedList = [...scopePreference];
      updatedList[existingIndex] = newSp;
      setScopePreference(updatedList);
    } else {
      setScopePreference([...(scopePreference || []), newSp]);
    }
  };

  const handleTemperatureUnitChange = async (unit) => {
    try {
      const currentPref = getCurrentScopePreference();
      const newSp = {
        ...currentPref,
        scopeId: activeScope,
        temperatureUnit: unit,
      };
      const resp = await saveScopePereference(sendAndWaitWs, newSp);
      if (!resp || resp.error) {
        errorToast("Error!");
      } else {
        successToast("Saved");
        // Update the context state immediately
        updateScopePreferenceList(newSp);
      }
    } catch (error) {
      console.error("Error saving preference:", error);
      errorToast("Failed to save preference");
    }
  };

  const handleDateFormatChange = async (format) => {
    try {
      const currentPref = getCurrentScopePreference();
      const newSp = {
        ...currentPref,
        scopeId: activeScope,
        dateFormat: format,
      };
      const resp = await saveScopePereference(sendAndWaitWs, newSp);
      if (!resp || resp.error) {
        errorToast("Error!");
      } else {
        successToast("Saved");
        updateScopePreferenceList(newSp);
      }
    } catch (error) {
      console.error("Error saving preference:", error);
      errorToast("Failed to save preference");
    }
  };

  const handleTimeFormatChange = async (format) => {
    try {
      const currentPref = getCurrentScopePreference();
      const newSp = {
        ...currentPref,
        scopeId: activeScope,
        timeFormat: format,
      };
      const resp = await saveScopePereference(sendAndWaitWs, newSp);
      if (!resp || resp.error) {
        errorToast("Error!");
      } else {
        successToast("Saved");
        updateScopePreferenceList(newSp);
      }
    } catch (error) {
      console.error("Error saving preference:", error);
      errorToast("Failed to save preference");
    }
  };

  const handleLengthUnitChange = async (unit) => {
    try {
      const currentPref = getCurrentScopePreference();
      const newSp = {
        ...currentPref,
        scopeId: activeScope,
        lengthUnit: unit,
      };
      const resp = await saveScopePereference(sendAndWaitWs, newSp);
      if (!resp || resp.error) {
        errorToast("Error!");
      } else {
        successToast("Saved");
        updateScopePreferenceList(newSp);
      }
    } catch (error) {
      console.error("Error saving preference:", error);
      errorToast("Failed to save preference");
    }
  };

  const handleWeightUnitChange = async (unit) => {
    try {
      const currentPref = getCurrentScopePreference();
      const newSp = {
        ...currentPref,
        scopeId: activeScope,
        weightUnit: unit,
      };
      const resp = await saveScopePereference(sendAndWaitWs, newSp);
      if (!resp || resp.error) {
        errorToast("Error!");
      } else {
        successToast("Saved");
        updateScopePreferenceList(newSp);
      }
    } catch (error) {
      console.error("Error saving preference:", error);
      errorToast("Failed to save preference");
    }
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        padding: spacingToPx(2),
        marginBottom: "100px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: spacingToPx(2),
          width: "100%",
        }}
      >
        {/* Temperature Unit Card */}
        <Card
          style={{
            width: "100%",
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            fontFamily: "Roboto, sans-serif",
            boxShadow: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              minHeight: "40px",
              paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
              paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
              paddingTop: spacingToPx(1),
              paddingBottom: spacingToPx(1),
            }}
          >
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Temperature Unit
            </Typography>
          </div>
          <Divider
            style={{
              marginBottom: spacingToPx(2),
              marginLeft: spacingToPx(2),
              marginRight: spacingToPx(2),
            }}
          />
          <div
            style={{
              paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
              paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
              paddingBottom: spacingToPx(isSmallScreen ? 1.5 : 2),
              display: "flex",
              flexDirection: "column",
              gap: spacingToPx(1),
            }}
          >
            <FormControl component="fieldset">
              <RadioGroup
                value={getCurrentScopePreference()?.temperatureUnit || ""}
                onChange={(event) => {
                  handleTemperatureUnitChange(event.target.value);
                }}
                style={{
                  alignItems: "flex-start",
                  gap: spacingToPx(2),
                }}
              >
                <FormControlLabel
                  value="F"
                  style={{
                    marginLeft: 0,
                    marginRight: 0,
                    alignItems: "center",
                  }}
                  control={<Radio className="text-muted-foreground" />}
                  label={
                    <Typography
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      °F
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="C"
                  style={{
                    marginLeft: 0,
                    marginRight: 0,
                    alignItems: "center",
                  }}
                  control={<Radio className="text-muted-foreground" />}
                  label={
                    <Typography
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      °C
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
          </div>
        </Card>

        {/* Date Format Card */}
        <Card
          style={{
            width: "100%",
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            fontFamily: "Roboto, sans-serif",
            boxShadow: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              minHeight: "40px",
              paddingLeft: spacingToPx(1.5),
              paddingRight: spacingToPx(1.5),
              paddingTop: spacingToPx(1),
              paddingBottom: spacingToPx(1),
            }}
          >
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Date Format
            </Typography>
          </div>
          <Divider
            style={{
              marginBottom: spacingToPx(2),
              marginLeft: spacingToPx(2),
              marginRight: spacingToPx(2),
            }}
          />
          <div
            style={{
              paddingLeft: spacingToPx(1.5),
              paddingRight: spacingToPx(1.5),
              paddingBottom: spacingToPx(1.5),
            }}
          >
            <FormControl component="fieldset">
              <RadioGroup
                value={getCurrentScopePreference()?.dateFormat || ""}
                onChange={(event) => {
                  handleDateFormatChange(event.target.value);
                }}
                style={{
                  alignItems: "flex-start",
                  gap: spacingToPx(2),
                }}
              >
                <FormControlLabel
                  value="yyyy-MM-dd"
                  style={{
                    marginLeft: 0,
                    marginRight: 0,
                    alignItems: "center",
                  }}
                  control={<Radio className="text-muted-foreground" />}
                  label={
                    <Typography
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      YYYY-MM-DD
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="MM/dd/yyyy"
                  style={{
                    marginLeft: 0,
                    marginRight: 0,
                    alignItems: "center",
                  }}
                  control={<Radio className="text-muted-foreground" />}
                  label={
                    <Typography
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      MM/DD/YYYY
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="dd/MM/yyyy"
                  style={{
                    marginLeft: 0,
                    marginRight: 0,
                    alignItems: "center",
                  }}
                  control={<Radio className="text-muted-foreground" />}
                  label={
                    <Typography
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      DD/MM/YYYY
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
          </div>
        </Card>

        {/* Time Format Card */}
        <Card
          style={{
            width: "100%",
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            fontFamily: "Roboto, sans-serif",
            boxShadow: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              minHeight: "40px",
              paddingLeft: spacingToPx(1.5),
              paddingRight: spacingToPx(1.5),
              paddingTop: spacingToPx(1),
              paddingBottom: spacingToPx(1),
            }}
          >
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Time Format
            </Typography>
          </div>
          <Divider
            style={{
              marginBottom: spacingToPx(2),
              marginLeft: spacingToPx(2),
              marginRight: spacingToPx(2),
            }}
          />
          <div
            style={{
              paddingLeft: spacingToPx(1.5),
              paddingRight: spacingToPx(1.5),
              paddingBottom: spacingToPx(1.5),
            }}
          >
            <FormControl component="fieldset">
              <RadioGroup
                value={getCurrentScopePreference()?.timeFormat || ""}
                onChange={(event) => {
                  handleTimeFormatChange(event.target.value);
                }}
                style={{
                  alignItems: "flex-start",
                  gap: spacingToPx(2),
                }}
              >
                <FormControlLabel
                  value="HH:mm"
                  style={{
                    marginLeft: 0,
                    marginRight: 0,
                    alignItems: "center",
                  }}
                  control={<Radio className="text-muted-foreground" />}
                  label={
                    <Typography
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      24 Hour
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="hh:mm a"
                  style={{
                    marginLeft: 0,
                    marginRight: 0,
                    alignItems: "center",
                  }}
                  control={<Radio className="text-muted-foreground" />}
                  label={
                    <Typography
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      12 Hour
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
          </div>
        </Card>

        {/* Length Unit Card */}
        <Card
          style={{
            width: "100%",
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            fontFamily: "Roboto, sans-serif",
            boxShadow: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              minHeight: "40px",
              paddingLeft: spacingToPx(1.5),
              paddingRight: spacingToPx(1.5),
              paddingTop: spacingToPx(1),
              paddingBottom: spacingToPx(1),
            }}
          >
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Length Unit
            </Typography>
          </div>
          <Divider
            style={{
              marginBottom: spacingToPx(2),
              marginLeft: spacingToPx(2),
              marginRight: spacingToPx(2),
            }}
          />
          <div
            style={{
              paddingLeft: spacingToPx(1.5),
              paddingRight: spacingToPx(1.5),
              paddingBottom: spacingToPx(1.5),
            }}
          >
            <FormControl component="fieldset">
              <RadioGroup
                value={getCurrentScopePreference()?.lengthUnit || ""}
                onChange={(event) => {
                  handleLengthUnitChange(event.target.value);
                }}
                style={{
                  alignItems: "flex-start",
                  gap: spacingToPx(2),
                }}
              >
                <FormControlLabel
                  value="metric"
                  style={{
                    marginLeft: 0,
                    marginRight: 0,
                    alignItems: "center",
                  }}
                  control={<Radio className="text-muted-foreground" />}
                  label={
                    <Typography
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      Metric (m, cm, km)
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="imperial"
                  style={{
                    marginLeft: 0,
                    marginRight: 0,
                    alignItems: "center",
                  }}
                  control={<Radio className="text-muted-foreground" />}
                  label={
                    <Typography
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      Imperial (ft, in, mi)
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
          </div>
        </Card>

        {/* Weight Unit Card */}
        <Card
          style={{
            width: "100%",
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            fontFamily: "Roboto, sans-serif",
            boxShadow: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              minHeight: "40px",
              paddingLeft: spacingToPx(1.5),
              paddingRight: spacingToPx(1.5),
              paddingTop: spacingToPx(1),
              paddingBottom: spacingToPx(1),
            }}
          >
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Weight Unit
            </Typography>
          </div>
          <Divider
            style={{
              marginBottom: spacingToPx(2),
              marginLeft: spacingToPx(2),
              marginRight: spacingToPx(2),
            }}
          />
          <div
            style={{
              paddingLeft: spacingToPx(1.5),
              paddingRight: spacingToPx(1.5),
              paddingBottom: spacingToPx(1.5),
            }}
          >
            <FormControl component="fieldset">
              <RadioGroup
                value={getCurrentScopePreference()?.weightUnit || ""}
                onChange={(event) => {
                  handleWeightUnitChange(event.target.value);
                }}
                style={{
                  alignItems: "flex-start",
                  gap: spacingToPx(2),
                }}
              >
                <FormControlLabel
                  value="metric"
                  style={{
                    marginLeft: 0,
                    marginRight: 0,
                    alignItems: "center",
                  }}
                  control={<Radio className="text-muted-foreground" />}
                  label={
                    <Typography
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      Metric (kg, g)
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="imperial"
                  style={{
                    marginLeft: 0,
                    marginRight: 0,
                    alignItems: "center",
                  }}
                  control={<Radio className="text-muted-foreground" />}
                  label={
                    <Typography
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      Imperial (lb, oz)
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Preference;
